import * as React from 'react';
import { useCallback, useEffect, SetStateAction, Dispatch } from 'react';
import usePlacesAutocomplete from 'use-places-autocomplete';

import { MapPinIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

import { MarkerPinIcon } from '@restworld/utils-common';

import { ChipCheckBox } from '../checkbox';
import { Heading } from '../heading';
import type { IconComponentType } from '../icon-button';

import { GoogleAutoCompleteDropdownItemProps, GooglePlacePrediction } from './google-auto-complete-input';
import { GoogleAutoCompleteAddressItemStyled } from './google-auto-complete-input.styles';

// for esp. employer onboarding page
const AddressSelectDropdown = ({
  data,
  setCardOverlay,
  onLocationInputChange,
  onSuggestionSelect,
  dropdownItemProps,
}: {
  data: GooglePlacePrediction[];
} & RenderSuggestionsProps) => {
  const { onClick, activeItem } = dropdownItemProps || {};
  return (
    <>
      {data.map((suggestion, index) => {
        const handleClick = () => {
          if (onClick) {
            onClick(place_id, `${main_text} ${secondary_text || ''}`);
            return;
          }
          setCardOverlay(false);
          onLocationInputChange(`${main_text} ${secondary_text || ''}`);
          onSuggestionSelect(suggestion);
        };
        const {
          place_id,
          structured_formatting: { main_text, secondary_text },
        } = suggestion;
        const isActiveItem = activeItem === place_id;
        return (
          <li
            key={place_id}
            onClick={handleClick}
            data-cy={`address-suggestion-${index}`}
            aria-label="restaurant-places"
          >
            <ChipCheckBox
              Icon={MapPinIcon as unknown as IconComponentType}
              checked={isActiveItem}
              id={place_id}
              label={`${main_text}${secondary_text ? `, ${secondary_text}` : ''}`}
              wrapperStyles={'w-full'}
            />
          </li>
        );
      })}
      {dropdownItemProps?.item}
    </>
  );
};

const AddressDirectApplyList = ({
  data,
  setCardOverlay,
  onLocationInputChange,
  onSuggestionSelect,
  dropdownItemProps,
}: {
  data: GooglePlacePrediction[];
} & RenderSuggestionsProps) => {
  const { activeItem, onClick, itemTextStyle, suffixIcon, dropdownItemStyle, activeItemStyle } =
    dropdownItemProps || {};
  return (
    <>
      {data.map((suggestion, index) => {
        const handleClick = () => {
          if (onClick) {
            onClick(place_id, `${main_text} ${secondary_text || ''}`);
            return;
          }
          setCardOverlay(false);
          onLocationInputChange(`${main_text} ${secondary_text || ''}`);
          onSuggestionSelect(suggestion);
        };
        const {
          place_id,
          structured_formatting: { main_text, secondary_text },
        } = suggestion;
        const isActiveItem = activeItem === place_id;
        return (
          <GoogleAutoCompleteAddressItemStyled
            data-cy={`address-suggestion-${index}`}
            key={place_id}
            onClick={handleClick}
            dropdownItemStyle={dropdownItemStyle}
            isActive={isActiveItem}
            activeItemStyle={activeItemStyle}
            aria-label="restaurant-places"
          >
            <div className="flex-row flex gap-2 items-center w-full">
              <MarkerPinIcon className="max-h-[18px] min-h-[18px] max-w-[18px] min-w-[18px] text-gray-500" />
              <div className="flex flex-col gap-1 flex-wrap w-full text-left max-w-[95%]">
                <Heading as="span" size={6} weight="normal" className="leading-5.25">
                  {main_text}
                </Heading>
                {main_text && secondary_text && (
                  <Heading
                    as="span"
                    size={6}
                    weight="normal"
                    className={itemTextStyle ? itemTextStyle : 'leading-5.25'}
                  >{`Via ${secondary_text}`}</Heading>
                )}
              </div>
            </div>
            {suffixIcon}
          </GoogleAutoCompleteAddressItemStyled>
        );
      })}
    </>
  );
};

/** Geographic bounding box used to bias autocomplete predictions (LatLngBoundsLiteral). */
export type PlacesLocationBias = { south: number; west: number; north: number; east: number };

const usePlacesAutocompleteLogic = ({
  placesFilterTypes,
  isScriptReady,
  sessionTokenRef,
  placesLocationBias,
}: {
  placesFilterTypes?: string[];
  isScriptReady?: boolean;
  sessionTokenRef: React.MutableRefObject<unknown>;
  placesLocationBias?: PlacesLocationBias;
}) => {
  useEffect(() => {
    if (isScriptReady && typeof window !== 'undefined' && (window as any).google && !sessionTokenRef.current) {
      const googleMapsPlaces = (window as any).google?.maps?.places;
      if (googleMapsPlaces) sessionTokenRef.current = new googleMapsPlaces.AutocompleteSessionToken();
    }
  }, [isScriptReady, sessionTokenRef]);

  const {
    ready,
    suggestions: { status, data },
    setValue,
    init,
  } = usePlacesAutocomplete({
    initOnMount: false,
    debounce: 300,
    requestOptions: {
      ...(placesFilterTypes ? { types: placesFilterTypes } : {}),
      ...(placesLocationBias ? { bounds: placesLocationBias } : {}),
      sessionToken:
        typeof window !== 'undefined' &&
        ((window as any) || {})?.google?.maps?.places &&
        sessionTokenRef.current instanceof (window as any).google.maps.places.AutocompleteSessionToken
          ? sessionTokenRef.current
          : undefined,
    },
    cache: 24 * 60 * 60,
  });
  return {
    ready,
    status,
    data,
    setValue,
    init,
  };
};

type RenderSuggestionsProps = {
  setCardOverlay: Dispatch<SetStateAction<boolean>>;
  placesFilterTypes?: string[];
  placesLocationBias?: PlacesLocationBias;
  onStatusChange?: (status: string) => void;
  onSuggestionSelect: (suggestion: GooglePlacePrediction) => void;
  formId: string;
  locationInputValue: string;
  onLocationInputChange: (v: string) => void;
  dropdownItemProps?: GoogleAutoCompleteDropdownItemProps;
  isScriptReady?: boolean;
  sessionTokenRef: React.MutableRefObject<unknown>;
};

export const useRenderSuggestions = (props: RenderSuggestionsProps) => {
  const {
    placesFilterTypes,
    locationInputValue,
    dropdownItemProps,
    isScriptReady,
    sessionTokenRef,
    placesLocationBias,
    onStatusChange,
  } = props;
  const { ready, status, data, setValue, init } = usePlacesAutocompleteLogic({
    placesFilterTypes,
    isScriptReady,
    sessionTokenRef,
    placesLocationBias,
  });

  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);
  const { itemRenderVariant = 'with_break_line', itemTextStyle } = dropdownItemProps || {};

  const renderSuggestions = useCallback(() => {
    return status === 'OK' ? (
      itemRenderVariant === 'with_break_line' ? (
        <AddressDirectApplyList data={data} {...props} />
      ) : (
        <AddressSelectDropdown data={data} {...props} />
      )
    ) : status === 'ZERO_RESULTS' ? (
      dropdownItemProps?.noResultElement ? (
        dropdownItemProps?.noResultElement
      ) : (
        <li>
          <span className="flex items-center gap-1">
            <ExclamationCircleIcon className="h-5 w-5 text-neutral-700" />
            <Heading
              as="span"
              size={6}
              weight="normal"
              color="neutral"
              className={itemTextStyle ? itemTextStyle : 'leading-5.25'}
            >{`Nessun risultato trovato`}</Heading>
          </span>
        </li>
      )
    ) : (
      dropdownItemProps?.loadingElement || (
        <li>
          <Heading
            as="span"
            size={6}
            weight="normal"
            color="neutral"
            className={itemTextStyle ? itemTextStyle : 'leading-5.25'}
          >{`loading...`}</Heading>
        </li>
      )
    );
  }, [
    data,
    dropdownItemProps?.loadingElement,
    dropdownItemProps?.noResultElement,
    itemRenderVariant,
    itemTextStyle,
    props,
    status,
  ]);

  useEffect(() => {
    try {
      if (locationInputValue) init();
      setValue(locationInputValue);
    } catch (error) {
      console.error('Error initializing autocomplete:', error);
    }
  }, [init, locationInputValue, setValue]);

  return {
    ready,
    renderSuggestions,
    status,
  };
};
